/**
 * Local settle mathematics - the same composition the server performs in
 * `settleWithin` (apps/api/src/modules/pos/service.ts), built from the SAME
 * shared primitives so a locally computed invoice is paise-identical to a
 * server-computed one. This module adds NO arithmetic of its own: two-tier
 * discounting and tax come from @kortex/shared exactly as the server uses
 * them. The parity tests in tests/totals-parity.test.ts hold this to worked
 * examples mirrored from the server's own test corpus.
 *
 * Unit conventions at this boundary (schema.ts): paise integers, quantities
 * in milli-units, rates in basis points.
 */
import { apportionDiscount, extraCharges, itemDiscountPaise, principalGstRate, taxLine, totalise, mulQty, } from '@kortex/shared';
import { LocalDataError } from '../types.js';
export const qtyFromMilli = (quantityMilli) => quantityMilli / 1000;
export const rateFromBp = (bp) => bp / 100;
/**
 * Mirrors the server's `taxedLines` + `totalise`: the item's own percentage
 * comes off first, then whatever an offer claimed, then the bill discount is
 * apportioned over what remains (largest-remainder, so no paisa is lost), and
 * GST is charged on the rest.
 */
export function computeSettle(lines, billDiscountPaise = 0, interState = false, 
/** What an offer took off each line, same order as `lines` (mirrors the
 *  server's `taxedLines(..., offers.perLine)`). */
offerDiscountsPaise = []) {
    if (lines.length === 0) {
        throw new LocalDataError('EMPTY_ORDER', 'Add at least one item before billing');
    }
    if (billDiscountPaise < 0) {
        throw new LocalDataError('INVALID_DISCOUNT', 'Discount cannot be negative');
    }
    const itemDiscounts = lines.map((line) => itemDiscountPaise(line.unitPricePaise, qtyFromMilli(line.quantityMilli), rateFromBp(line.discountPctBp)));
    const grossPerLine = lines.map((line) => mulQty(line.unitPricePaise, qtyFromMilli(line.quantityMilli)));
    const gross = grossPerLine.reduce((a, b) => a + b, 0);
    const itemDiscount = itemDiscounts.reduce((a, b) => a + b, 0);
    const offerDiscount = lines.reduce((sum, _, i) => sum + (offerDiscountsPaise[i] ?? 0), 0);
    if (billDiscountPaise > gross - itemDiscount - offerDiscount) {
        throw new LocalDataError('DISCOUNT_TOO_LARGE', 'The discount is larger than the ticket');
    }
    const remaining = lines.map((_, i) => Math.max(0, (grossPerLine[i] ?? 0) - (itemDiscounts[i] ?? 0) - (offerDiscountsPaise[i] ?? 0)));
    const shares = apportionDiscount(remaining.map((value) => ({ unitPrice: value, quantity: 1 })), billDiscountPaise);
    const taxed = lines.map((line, i) => taxLine({
        unitPrice: line.unitPricePaise,
        quantity: qtyFromMilli(line.quantityMilli),
        gstRate: rateFromBp(line.gstRateBp),
        discount: (itemDiscounts[i] ?? 0) + (offerDiscountsPaise[i] ?? 0) + (shares[i] ?? 0),
        interState,
    }));
    return { taxed, totals: totalise(taxed), itemDiscount, offerDiscount, gross };
}
/**
 * The charge lines a ticket carries, taxed at the food's own rate - a direct
 * port of the server's `chargeLines` (apps/api/src/modules/pos/service.ts),
 * over the same shared `extraCharges`/`principalGstRate`/`taxLine` primitives.
 */
export function computeCharges(foodTaxed, input) {
    const net = foodTaxed.reduce((sum, line) => sum + line.taxable, 0);
    const gstRate = principalGstRate(foodTaxed.map((l) => ({ taxable: l.taxable, gstRate: l.gstRate })));
    return extraCharges({ orderType: input.orderType, net, settings: input.settings }).map((charge) => ({
        charge,
        taxed: taxLine({ unitPrice: charge.amount, quantity: 1, gstRate, interState: input.interState }),
    }));
}
//# sourceMappingURL=totals.js.map